import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { twMerge } from "tailwind-merge";
import BackButton from "../components/BackButton";
import Button from "../components/Button";
import ConfirmationDialog from "../components/ConfirmationDialog";
import MergeBatchModal from "../components/batches/MergeBatchModal";
import Map from "../components/Map";
import useBatchDetails from "../hooks/logics/batches/use-batch-details";
import { formatPrices, batchStatusMap } from "../utils/helpers";
import { FiTrash2 } from "react-icons/fi";
import { BiGitMerge } from "react-icons/bi";

const getInitials = (name?: string) => {
    if (!name) return "NA";
    return name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .substring(0, 2);
};

const getEventLabel = (event: string) => {
    switch (event) {
        case "batch_opened":
            return "Batch Created";
        case "order_added":
            return "Order Added";
        case "batch_closed":
            return "Batch Finalized";
        case "dispatched":
            return "Batch Dispatched";
        case "completed":
            return "Batch Completed";
        case "driver_assigned":
            return "Driver Assigned";
        case "batch_merged_into":
            return "Batch Merged";
        case "batch_merged_from":
            return "Orders Absorbed from Batch";
        default:
            return event.replace(/_/g, " ");
    }
};

const getEventDesc = (event: string, log: any) => {
    switch (event) {
        case "batch_opened":
            return "Initial route setup finalized";
        case "order_added":
            return "Order was added to the batch";
        case "batch_closed":
            return `${log.metadata?.totalOrders || "N/A"} orders from ${log.metadata?.merchantCount || "N/A"} merchants grouped`;
        case "dispatched":
            return "Broadcast sent to drivers";
        case "completed":
            return "Batch completed successfully";
        case "driver_assigned":
            return `${log.metadata?.driverName || "Driver"} accepted the batch`;
        case "batch_merged_into":
            return `Merged into Batch ${log.metadata?.targetBatchId ? `#${log.metadata.targetBatchId.slice(-6)}` : ''} (${log.metadata?.ordersMovedCount || 0} orders moved)`;
        case "batch_merged_from":
            return `Absorbed ${log.metadata?.ordersAbsorbedCount || 0} orders from Batch ${log.metadata?.sourceBatchId ? `#${log.metadata.sourceBatchId.slice(-6)}` : ''}`;
        default:
            return `Triggered by ${log.triggeredBy}`;
    }
};

const getEventColor = (event: string) => {
    switch (event) {
        case "completed":
            return "bg-green-500";
        case "driver_assigned":
            return "bg-green-500";
        case "batch_closed":
            return "bg-blue-500";
        case "batch_merged_into":
        case "batch_merged_from":
            return "bg-purple-500";
        case "batch_opened":
            return "bg-gray-400";
        default:
            return "bg-blue-400";
    }
};

const groupOrdersByMerchant = (orders: any[]) => {
    const groups: { [key: string]: { merchant: any; orders: any[] } } = {};
    orders.forEach((order) => {
        const user = Array.isArray(order.user) ? order.user[0] : order.user;
        const userId = user?._id || "unknown";
        if (!groups[userId]) {
            groups[userId] = {
                merchant: user,
                orders: [],
            };
        }
        groups[userId].orders.push(order);
    });
    return Object.values(groups);
};

const BatchDetails = () => {
    const navigate = useNavigate();
    const [isMergeModalOpen, setIsMergeModalOpen] = useState(false);
    const {
        batchInfo,
        orders,
        pricing,
        logs,
        loading,
        isClosingBatch,
        handleBatchClose,
        orderToRemove,
        setOrderToRemove,
        isRemovingOrder,
        handleConfirmRemoveOrder,
    } = useBatchDetails();



    const formattedDate = batchInfo?.createdAt
        ? new Date(batchInfo.createdAt).toLocaleString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        })
        : "N/A";

    const merchantGroups = groupOrdersByMerchant(orders);

    const pickups: any[] = [];
    const deliveries: any[] = [];
    const routes: any[] = [];

    orders.forEach((order, idx) => {
        const pickupLoc = order.serviceMeta?.pickupLocation;
        const deliveryLoc = order.serviceMeta?.deliveryLocation;

        const pLat = Number(pickupLoc?.lat);
        const pLng = Number(pickupLoc?.lng);
        const dLat = Number(deliveryLoc?.lat);
        const dLng = Number(deliveryLoc?.lng);

        const hasPickup = !isNaN(pLat) && !isNaN(pLng) && pLat !== 0 && pLng !== 0;
        const hasDelivery = !isNaN(dLat) && !isNaN(dLng) && dLat !== 0 && dLng !== 0;

        const merchantUser = Array.isArray(order.user) ? order.user[0] : order.user;
        const merchantName = merchantUser?.fullName || `Merchant ${idx + 1}`;
        const customerName = deliveryLoc?.contactName || `Customer ${idx + 1}`;

        let pickupPoint: any = null;
        let deliveryPoint: any = null;

        if (hasPickup) {
            pickupPoint = {
                id: `pickup-${order._id || idx}`,
                lat: pLat,
                lng: pLng,
                name: merchantName,
                address: pickupLoc.address || "Pickup Address",
                label: `Pickup: ${merchantName}`,
                type: "pickup",
            };
            pickups.push(pickupPoint);
        }

        if (hasDelivery) {
            deliveryPoint = {
                id: `delivery-${order._id || idx}`,
                lat: dLat,
                lng: dLng,
                name: customerName,
                address: deliveryLoc.address || "Delivery Address",
                label: `Delivery: ${customerName}`,
                type: "delivery",
            };
            deliveries.push(deliveryPoint);
        }

        if (pickupPoint && deliveryPoint) {
            routes.push({
                from: pickupPoint,
                to: deliveryPoint,
                color: "#3B82F6",
            });
        }
    });

    return (
        <div className="w-full h-full min-h-screen py-6 md:py-10 bg-gray-100 font-inter">
            <BackButton link="/dashboard/batches" />

            {loading ? (
                <div className="w-full flex items-center justify-center py-20">
                    <div className="loader"></div>
                </div>
            ) : (
                <div className="flex flex-col gap-6">
                    {/* Header Card */}
                    <div className="w-full bg-white rounded-xl shadow-sm border border-gray-100 p-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="flex flex-wrap items-center gap-3">
                            <h1 className="text-xl md:text-2xl font-bold text-gray-800">
                                Batch ID: {batchInfo?._id || "N/A"}
                            </h1>
                            {batchStatusMap(
                                batchInfo?.status || "",
                                twMerge(
                                    "text-xs md:text-sm font-semibold px-3 py-1 rounded-full capitalize",
                                    batchInfo?.status === "completed" && "bg-green-100 text-green-800",
                                    batchInfo?.status === "cancelled" && "bg-red-100 text-red-800",
                                    batchInfo?.status === "merged" && "bg-gray-100 text-gray-800",
                                    batchInfo?.status === "in_progress" && "bg-blue-100 text-blue-800",
                                    batchInfo?.status === "forming" && "bg-purple-100 text-purple-800",
                                    batchInfo?.status === "dispatched" && "bg-orange-100 text-orange-800",
                                    batchInfo?.status === "pending_payment" && "bg-yellow-100 text-yellow-800"
                                )
                            )}
                        </div>
                        <div className="flex items-center gap-3">
                            {batchInfo?.status === "forming" && (
                                <>
                                    <Button
                                        type="button"
                                        onClick={() => setIsMergeModalOpen(true)}
                                        className="min-h-0 h-9 px-4 text-xs font-semibold bg-primary hover:bg-primary/90 text-white rounded-lg transition-colors flex items-center gap-1.5"
                                    >
                                        <BiGitMerge className="text-base" />
                                        <span>Merge Batch</span>
                                    </Button>
                                    <Button
                                        onClick={handleBatchClose}
                                        disabled={isClosingBatch}
                                        className="min-h-0 h-9 px-4 text-xs font-semibold bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors flex items-center gap-1.5"
                                    >
                                        {isClosingBatch ? "Closing..." : "Close Batch"}
                                    </Button>
                                </>
                            )}
                            <p className="text-sm text-gray-500">
                                Date Created: {formattedDate}
                            </p>
                        </div>
                    </div>

                    {/* Driver Card */}
                    {batchInfo?.driver && (
                        <div className="w-full bg-white rounded-xl shadow-sm border border-gray-100 p-6 flex items-center gap-4">
                            <div className="w-14 h-14 bg-purple-100 flex items-center justify-center rounded-full text-purple-700 font-bold text-xl">
                                {getInitials(batchInfo?.driver?.fullName)}
                            </div>
                            <div className="flex flex-col gap-1 flex-1">
                                <div className="flex items-center gap-2">
                                    <h2 className="text-lg font-bold text-gray-800">{batchInfo?.driver?.fullName}</h2>
                                    {batchInfo?.driver?.rating !== undefined && (
                                        <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded border border-green-500 text-green-700 bg-green-50">
                                            ★ {batchInfo?.driver?.rating.toFixed(1)}
                                        </span>
                                    )}
                                </div>
                                <div className="flex flex-wrap items-center gap-y-1 gap-x-4 text-sm text-gray-500">
                                    {batchInfo?.driver?.phone && <span className="flex items-center gap-1.5">📞 {batchInfo?.driver?.phone}</span>}
                                    {batchInfo?.driver?.email && <span className="flex items-center gap-1.5">✉️ {batchInfo?.driver?.email}</span>}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Orders List Grouped by Merchant */}
                    <div className="flex flex-col gap-4">
                        <h2 className="text-lg font-bold text-gray-800">
                            Orders ({merchantGroups.length} merchants · {orders.length} orders)
                        </h2>

                        {merchantGroups.map((group, idx) => {
                            /* const totalCharged = group.orders.reduce(
                                (sum, o) => sum + (Number(o.userTotal) || 0),
                                0
                            ); */

                            return (
                                <div
                                    key={idx}
                                    className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden flex flex-col"
                                >
                                    {/* Merchant Info Header */}
                                    <div className="bg-gray-50 px-4 sm:px-6 py-4 border-b border-gray-100 flex flex-col lg:flex-row lg:items-center justify-between gap-3 text-xs sm:text-sm">
                                        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 flex-wrap">
                                            <h3 className="font-bold text-gray-800 text-sm sm:text-base">
                                                {group.merchant?.fullName || "Store"}
                                            </h3>
                                            <div className="flex flex-wrap items-center gap-3 sm:gap-4 text-gray-500">
                                                {group.merchant?.phone && (
                                                    <span className="flex items-center gap-1 whitespace-nowrap">
                                                        📞 {group.merchant.phone}
                                                    </span>
                                                )}
                                                {group.merchant?.email && (
                                                    <span className="flex items-center gap-1 break-all">
                                                        ✉️ {group.merchant.email}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        {group.orders[0]?.serviceMeta?.pickupLocation && (
                                            <p className="text-gray-500 max-w-xl text-left lg:text-right">
                                                Pickup location:{" "}
                                                <span className="text-gray-700 font-medium">
                                                    {group.orders[0].serviceMeta.pickupLocation.address}
                                                </span>
                                            </p>
                                        )}
                                    </div>

                                    {/* Merchant Orders List */}
                                    <div className="flex flex-col divide-y divide-gray-100 px-4 sm:px-6">
                                        {group.orders.map((order, oIdx) => (
                                            <div
                                                key={oIdx}
                                                className="py-4 flex flex-col lg:flex-row lg:items-center justify-between gap-4 text-sm"
                                            >
                                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 items-start lg:items-center gap-3 lg:gap-4 flex-1">
                                                    <div className="lg:col-span-3">
                                                        <Link to={`/dashboard/orders/${order._id}`}>
                                                            <p className="font-semibold font-mono text-xs sm:text-sm text-primary hover:underline break-all">
                                                                #{order._id}
                                                            </p>
                                                        </Link>
                                                    </div>
                                                    <div className="lg:col-span-3">
                                                        <p className="font-medium text-gray-800">
                                                            {order.serviceMeta?.deliveryLocation?.contactName ||
                                                                "N/A"}
                                                        </p>
                                                        <p className="text-xs text-gray-500">
                                                            {order.serviceMeta?.deliveryLocation?.phone ||
                                                                "N/A"}
                                                        </p>
                                                    </div>
                                                    <div className="lg:col-span-6">
                                                        <p className="text-gray-600 text-xs sm:text-sm">
                                                            {order.serviceMeta?.deliveryLocation?.address ||
                                                                "N/A"}
                                                        </p>
                                                    </div>
                                                </div>
                                                <div className="flex flex-wrap items-center justify-between lg:justify-end gap-3 sm:gap-4 shrink-0 pt-2 lg:pt-0 border-t border-gray-100 lg:border-t-0">
                                                    <p className="text-xs sm:text-sm text-gray-500 whitespace-nowrap">
                                                        Pickup pin:{" "}
                                                        <span className="font-semibold text-gray-800 font-mono">
                                                            {order.pickupPin || "N/A"}
                                                        </span>
                                                    </p>
                                                    <p className="text-xs sm:text-sm text-gray-500 whitespace-nowrap">
                                                        Delivery Pin:{" "}
                                                        <span className="font-semibold text-gray-800 font-mono">
                                                            {order.pin || "N/A"}
                                                        </span>
                                                    </p>
                                                    <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-100 capitalize whitespace-nowrap">
                                                        {order.status ? order.status.replace(/([A-Z])/g, " $1") : "Assigned"}
                                                    </span>
                                                    {batchInfo?.status === "forming" && (
                                                        <button
                                                            type="button"
                                                            onClick={() => setOrderToRemove(order._id)}
                                                            className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-red-600 hover:text-white bg-red-50 hover:bg-red-600 border border-red-200 hover:border-red-600 rounded-lg transition-all duration-150 shadow-sm cursor-pointer whitespace-nowrap"
                                                            title="Remove order from batch"
                                                        >
                                                            <FiTrash2 className="w-3.5 h-3.5" />
                                                            <span>Remove</span>
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Bottom Summary Grid */}
                    <div className={twMerge(
                        "grid grid-cols-1 gap-6",
                        batchInfo?.status !== "cancelled" && batchInfo?.status !== "merged" && "lg:grid-cols-5"
                    )}>
                        {/* Payment Summary */}
                        <div className={twMerge(
                            "bg-white rounded-xl shadow-sm border border-gray-100 p-6 flex flex-col",
                            batchInfo?.status !== "cancelled" && batchInfo?.status !== "merged" ? "lg:col-span-2" : "w-full"
                        )}>
                            <h2 className="text-lg font-bold text-gray-800 mb-4 pb-2 border-b border-gray-100">
                                Payment summary
                            </h2>
                            <div className="flex flex-col gap-3 text-sm mb-4">
                                <p className="text-gray-500 font-semibold mb-1">Collected from Merchants</p>
                                {merchantGroups.map((group, idx) => {
                                    const totalCharged = group.orders.reduce(
                                        (sum, o) => sum + (Number(o.userTotal) || Number(o.discountedUserTotal) || 0),
                                        0
                                    );
                                    return (
                                        <div key={idx} className="flex justify-between">
                                            <span className="text-gray-600">
                                                {group.merchant?.fullName || `Merchant ${idx + 1}`}
                                            </span>
                                            <span className="font-medium text-gray-800">
                                                {formatPrices(totalCharged)}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>

                            <hr className="border-gray-100 mb-4" />

                            <div className="flex flex-col gap-3 text-sm">
                                <div className="flex justify-between items-center">
                                    <span className="text-gray-600 font-bold">Total Collected</span>
                                    <span className="font-bold text-green-600 text-lg">
                                        {formatPrices(pricing?.totalUserAmount || orders.reduce((sum, o) => sum + (Number(o.userTotal) || 0), 0))}
                                    </span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Driver Received</span>
                                    <span className="font-semibold text-gray-800">
                                        {formatPrices(pricing?.totalDriverAmount || orders.reduce((sum, o) => sum + (Number(o.driverTotal) || 0), 0))}
                                    </span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Tip</span>
                                    <span className="text-gray-400 font-normal">{formatPrices(pricing?.tip || 0)}</span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-gray-600 font-bold">Swyvery total</span>
                                    <span className="font-bold text-green-600 text-lg">
                                        {formatPrices(Number(pricing?.totalServiceCharges))}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Map Container */}
                        {batchInfo?.status !== "cancelled" && batchInfo?.status !== "merged" && (
                            <div className="lg:col-span-3 bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden relative min-h-[350px]">
                                {pickups.length > 0 || deliveries.length > 0 ? (
                                    <Map
                                        pickups={pickups}
                                        deliveries={deliveries}
                                        routes={routes}
                                        mapContainerClassName="absolute inset-0 w-full h-full"
                                    />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center text-gray-400 bg-gray-50/50">
                                        Map unavailable for this batch
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Logs Timeline */}
                    <div className="w-full bg-white rounded-xl shadow-sm border border-gray-100 p-6 flex flex-col">
                        <h2 className="text-lg font-bold text-gray-800 mb-6 pb-2 border-b border-gray-100">
                            Logs
                        </h2>
                        {logs && logs.length > 0 ? (
                            <div className="relative border-l border-gray-200 ml-4 flex flex-col gap-6">
                                {logs.map((log, idx) => {
                                    const time = new Date(log.createdAt).toLocaleTimeString("en-US", {
                                        hour: "2-digit",
                                        minute: "2-digit",
                                    });

                                    return (
                                        <div key={idx} className="relative pl-6 flex items-start text-sm">
                                            {/* Log status icon circle indicator */}
                                            <span
                                                className={twMerge(
                                                    "absolute -left-[6px] top-1 h-3 w-3 rounded-full border border-white ring-4 ring-white",
                                                    getEventColor(log.event)
                                                )}
                                            ></span>
                                            <div className="flex flex-col md:flex-row md:items-center gap-1.5 md:gap-4 flex-1">
                                                <span className="font-bold text-gray-800 whitespace-nowrap min-w-[70px]">
                                                    {time}
                                                </span>
                                                <span className="font-semibold text-gray-700 min-w-[120px]">
                                                    {getEventLabel(log.event)}
                                                </span>
                                                <span className="text-gray-500 font-normal">
                                                    • {getEventDesc(log.event, log)}
                                                </span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="text-center py-6 text-gray-400">No logs recorded for this batch</div>
                        )}
                    </div>
                </div>
            )}

            <ConfirmationDialog
                isVisible={Boolean(orderToRemove)}
                onHide={() => setOrderToRemove(null)}
                handleClick={handleConfirmRemoveOrder}
                isSubmitting={isRemovingOrder}
                isSubmitButtonDisabled={isRemovingOrder}
                text="Are you sure you want to remove this order from this batch?"
            />

            <MergeBatchModal
                isVisible={isMergeModalOpen}
                onHide={() => setIsMergeModalOpen(false)}
                sourceBatch={batchInfo ? {
                    _id: batchInfo._id,
                    batchNumber: batchInfo.batchNumber,
                    totalOrders: orders.length || batchInfo.totalOrders,
                    totalPickUpStops: batchInfo.totalPickUpStops,
                    totalDropOffStops: batchInfo.totalDropOffStops,
                    type: batchInfo.type,
                    status: batchInfo.status,
                } : null}
                onSuccess={(mergedTargetBatchId) => {
                    navigate(`/dashboard/batches/${mergedTargetBatchId}`);
                }}
            />
        </div>
    );
};

export default BatchDetails;
